import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isClient } from '@/composables/useSSR'
import { fetchManifestHash, getInitialHash, hasVersionChanged } from '@/composables/useVersionCheck'

// Configuration constants
const POLL_INTERVAL_MS = 60_000 // Check every 60 seconds

export const useVersionStore = defineStore('version', () => {
  // State
  const manifestHash = ref<string | null>(null) // Hash when app was loaded
  const latestHash = ref<string | null>(null) // Latest hash from polling
  const watcherActive = ref(false) // Prevent duplicate watchers
  const dirtyFormIds = ref<string[]>([]) // Forms with unsaved changes (array for SSR serialization)

  // Computed
  const isUpdateAvailable = computed(() => {
    return hasVersionChanged(manifestHash.value, latestHash.value)
  })

  const hasDirtyForm = computed(() => {
    return dirtyFormIds.value.length > 0
  })

  // Event handlers (stored for cleanup)
  let pollInterval: ReturnType<typeof setInterval> | null = null
  let visibilityHandler: (() => void) | null = null

  /**
   * Fetch latest manifest and compare hashes
   */
  async function checkForUpdate(): Promise<boolean> {
    const hash = await fetchManifestHash()
    if (hash) {
      latestHash.value = hash
      const nowAvailable = hasVersionChanged(manifestHash.value, hash)

      if (nowAvailable) {
        console.log('[VersionStore] Update detected, will reload on next navigation')
      }

      return nowAvailable
    }
    return false
  }

  /**
   * Initialize the current version hash (call once at app start)
   * Uses getInitialHash() to read from current DOM - no network request needed
   */
  function initializeVersion(): void {
    const hash = getInitialHash()
    if (hash) {
      manifestHash.value = hash
      latestHash.value = hash
      console.log('[VersionStore] Initialized with hash:', hash)
    } else {
      console.log('[VersionStore] No script hash available (dev mode or non-standard build)')
    }
  }

  /**
   * Force trigger update available state (for testing)
   * Call from browser console: useVersionStore()._simulateUpdate()
   */
  function _simulateUpdate(): void {
    if (manifestHash.value) {
      latestHash.value = manifestHash.value + '-new'
    } else {
      manifestHash.value = 'test-hash'
      latestHash.value = 'test-hash-new'
    }
    console.log('[VersionStore] Simulated update - isUpdateAvailable:', isUpdateAvailable.value)
  }

  /**
   * Register a form as having unsaved changes
   */
  function registerDirtyForm(formId: string): void {
    if (!dirtyFormIds.value.includes(formId)) {
      dirtyFormIds.value = [...dirtyFormIds.value, formId]
    }
  }

  /**
   * Unregister a form from the dirty list (after save or unmount)
   */
  function unregisterDirtyForm(formId: string): void {
    dirtyFormIds.value = dirtyFormIds.value.filter((id) => id !== formId)
  }

  /**
   * Clear all dirty forms (after user confirms discard)
   */
  function clearAllDirtyForms(): void {
    dirtyFormIds.value = []
  }

  /**
   * Start the version watcher (polling + visibility listener)
   */
  function startVersionWatcher(): void {
    if (watcherActive.value || !isClient) return

    watcherActive.value = true

    // Initialize current version hash
    initializeVersion()

    // Visibility change handler - check for updates when tab becomes visible
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate()
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)

    // Periodic polling for new versions
    pollInterval = setInterval(() => {
      checkForUpdate()
    }, POLL_INTERVAL_MS)
  }

  /**
   * Stop the version watcher (cleanup)
   */
  function stopVersionWatcher(): void {
    if (!isClient) return

    // Remove visibility listener
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }

    // Clear polling interval
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }

    watcherActive.value = false
  }

  return {
    // State
    manifestHash,
    latestHash,

    // Computed
    isUpdateAvailable,
    hasDirtyForm,

    // Methods
    checkForUpdate,
    initializeVersion,
    registerDirtyForm,
    unregisterDirtyForm,
    clearAllDirtyForms,
    startVersionWatcher,
    stopVersionWatcher,

    // Debug/Testing
    _simulateUpdate
  }
})
